import { z } from "zod";
import { isOneOf } from "@remi/ts-utils";

import { RegionRole, RegionRoleDisplayNames } from "./region";

// Values are opaque `projrole_<random>` tokens so a role can be renamed later for free (rename the key, keep the
// value). NEW roles mint a token, not an identity value; the identity entries below are grandfathered. See
// docs/guides/migrations/rename-in-place.md.
const AssignableProjectRole = {
  ...RegionRole,
  QUOTE_OWNER: "QUOTE_OWNER",
  CONTRACTOR_PRODUCTION: "CONTRACTOR_PRODUCTION",
  FIELD_WORKER: "FIELD_WORKER",
  HOMEOWNER_RELATIONSHIP_MANAGER: "HOMEOWNER_RELATIONSHIP_MANAGER",
  SALES_DEVELOPMENT_REPRESENTATIVE: "SALES_DEVELOPMENT_REPRESENTATIVE",
  ESTIMATOR: "ESTIMATOR",
  INSURANCE_ADJUSTER: "INSURANCE_ADJUSTER",
  INSURANCE_CES: "INSURANCE_CES",
} as const;

export type AssignableProjectRole = (typeof AssignableProjectRole)[keyof typeof AssignableProjectRole];

export const AssignableProjectRoleValues: readonly AssignableProjectRole[] = Object.values(AssignableProjectRole);

// The roles `setProjectOwners` owns. FIELD_WORKER is multi-occupant — crew members must not evict each other —
// so it stays on `projectDao.addOwner` and a FIELD_WORKER call site fails to compile against the choke point.
export type OwnerProjectRole = Exclude<AssignableProjectRole, typeof AssignableProjectRole.FIELD_WORKER>;

// Owner roles whose slot legitimately holds a set of people (the production crew), so `setProjectOwners` must
// not reject two people naming the same slot in one call the way it does for single-occupant roles.
export const MultiOccupantOwnerProjectRoleValues = [
  AssignableProjectRole.CONTRACTOR_PRODUCTION,
] as const satisfies readonly OwnerProjectRole[];

export const AssignableProjectRoleSchema = z.enum(AssignableProjectRole);

// `exclude` takes the enum's keys, not its values.
export const OwnerProjectRoleSchema = AssignableProjectRoleSchema.exclude(["FIELD_WORKER"]);

export const OwnerProjectRoleValues: readonly OwnerProjectRole[] = Object.values(OwnerProjectRoleSchema.enum);

export const ProjectRole = { ...AssignableProjectRole, INVALID: "INVALID" } as const;

export type ProjectRole = (typeof ProjectRole)[keyof typeof ProjectRole];

export const ProjectRoleValues: readonly ProjectRole[] = Object.values(ProjectRole);

export const ProjectRoleSchema = z.enum(ProjectRole);

export const ProjectRoleReadSchema = ProjectRoleSchema.catch(ProjectRole.INVALID);

// The value Account Manager held before it left ProjectRole: still in persisted history/activity rows
// and the frozen tag the owners/mention synthesis puts on the worksite-sourced AM. Never re-minted or re-used.
export const LEGACY_ACCOUNT_MANAGER_PROJECT_ROLE_TOKEN = "projrole_dc8a5b08";

// The value Contractor Field Manager held before it was retired: still in persisted history/activity rows.
export const LEGACY_CONTRACTOR_FIELD_MANAGER_PROJECT_ROLE_TOKEN = "projrole_0b270969";

const LegacyProjectRoleTokens = [
  LEGACY_ACCOUNT_MANAGER_PROJECT_ROLE_TOKEN,
  LEGACY_CONTRACTOR_FIELD_MANAGER_PROJECT_ROLE_TOKEN,
] as const;

type LegacyProjectRoleToken = (typeof LegacyProjectRoleTokens)[number];

const LegacyProjectRoleTokenDisplayNames = {
  [LEGACY_ACCOUNT_MANAGER_PROJECT_ROLE_TOKEN]: "Account Manager",
  [LEGACY_CONTRACTOR_FIELD_MANAGER_PROJECT_ROLE_TOKEN]: "Contractor Field Manager",
} as const satisfies Record<LegacyProjectRoleToken, string>;

export const isLegacyProjectRoleToken = (value: string): value is LegacyProjectRoleToken =>
  isOneOf(value, LegacyProjectRoleTokens);

// Persisted PersonToProject/history rows still hold this literal; ProjectRoleReadSchema would coerce it to INVALID.
export const RETIRED_PROJECT_MANAGER_PROJECT_ROLE_TOKEN = "PROJECT_MANAGER";

// Read-side owner roles only: the synthesis splices this token onto the worksite AM. Write inputs stay
// ProjectRole — the token is rejected at the AssignableProjectRoleSchema boundary.
export type ProjectRoleOrLegacyAccountManager = ProjectRole | typeof LEGACY_ACCOUNT_MANAGER_PROJECT_ROLE_TOKEN;

export const ProjectRoleOrLegacyAccountManagerSchema = z.union([
  ProjectRoleSchema,
  z.literal(LEGACY_ACCOUNT_MANAGER_PROJECT_ROLE_TOKEN),
]);

export const ProjectRoleDisplayValues: Readonly<Record<ProjectRole, string>> = {
  ...RegionRoleDisplayNames,
  QUOTE_OWNER: "Quote Owner",
  CONTRACTOR_PRODUCTION: "Contractor Production",
  FIELD_WORKER: "Field Worker",
  HOMEOWNER_RELATIONSHIP_MANAGER: "HSA",
  SALES_DEVELOPMENT_REPRESENTATIVE: "SDR",
  ESTIMATOR: "Estimator",
  INSURANCE_ADJUSTER: "Insurance Adjuster",
  INSURANCE_CES: "Customer Experience Specialist",
  INVALID: "Invalid",
};

export const getProjectRoleDisplayName = (role: string): string => {
  if (isLegacyProjectRoleToken(role)) {
    return LegacyProjectRoleTokenDisplayNames[role];
  }
  if (role === RETIRED_PROJECT_MANAGER_PROJECT_ROLE_TOKEN) {
    return "Project Manager (retired)";
  }
  return isOneOf(role, ProjectRoleValues)
    ? ProjectRoleDisplayValues[role]
    : ProjectRoleDisplayValues[ProjectRole.INVALID];
};

export const MentionableProjectRole = {
  REGIONAL_MANAGER: RegionRole.REGIONAL_MANAGER,
  RLL_MANAGER: RegionRole.RLL_MANAGER,
  MATERIALS_COORDINATOR: RegionRole.MATERIALS_COORDINATOR,
  PERMITTING_ADMIN: RegionRole.PERMITTING_ADMIN,
  CORPORATE_PARTNER_POC: RegionRole.CORPORATE_PARTNER_POC,
  QUOTE_OWNER: ProjectRole.QUOTE_OWNER,
  HOMEOWNER_RELATIONSHIP_MANAGER: ProjectRole.HOMEOWNER_RELATIONSHIP_MANAGER,
  SALES_DEVELOPMENT_REPRESENTATIVE: ProjectRole.SALES_DEVELOPMENT_REPRESENTATIVE,
  ESTIMATOR: ProjectRole.ESTIMATOR,
} as const;

export type MentionableProjectRole = (typeof MentionableProjectRole)[keyof typeof MentionableProjectRole];

export const MentionableProjectRoleValues: readonly MentionableProjectRole[] = Object.values(MentionableProjectRole);

// Mention/taggable reads carry the frozen legacy token on the worksite-sourced AM alongside the live
// mentionable roles.
export type MentionableProjectRoleOrLegacyAccountManager =
  | MentionableProjectRole
  | typeof LEGACY_ACCOUNT_MANAGER_PROJECT_ROLE_TOKEN;

const MentionableProjectRoleDisplayValues: Readonly<Record<MentionableProjectRole, string>> = {
  CORPORATE_PARTNER_POC: "CPM",
  HOMEOWNER_RELATIONSHIP_MANAGER: "HSA",
  SALES_DEVELOPMENT_REPRESENTATIVE: "SDR",
  QUOTE_OWNER: "Quote Owner",
  REGIONAL_MANAGER: "Regional Manager",
  RLL_MANAGER: "RLL Manager",
  MATERIALS_COORDINATOR: "Materials Coordinator",
  PERMITTING_ADMIN: "Permitting Admin",
  ESTIMATOR: "Estimator",
};

export const getMentionableProjectRoleDisplayName = (role: string): string => {
  if (role === LEGACY_ACCOUNT_MANAGER_PROJECT_ROLE_TOKEN) {
    return "Account Manager";
  }
  return isOneOf(role, MentionableProjectRoleValues) ? MentionableProjectRoleDisplayValues[role] : role;
};

export const BidContactProjectRoleValues = [ProjectRole.HOMEOWNER_RELATIONSHIP_MANAGER, ProjectRole.ESTIMATOR] as const;

export const BidContactProjectRoleSchema = z.enum(BidContactProjectRoleValues);

export type BidContactProjectRole = (typeof BidContactProjectRoleValues)[number];

export const PartnerContactProjectRoleValues = [ProjectRole.INSURANCE_ADJUSTER, ProjectRole.INSURANCE_CES] as const;

export const PartnerContactProjectRoleSchema = z.enum(PartnerContactProjectRoleValues);

export type PartnerContactProjectRole = (typeof PartnerContactProjectRoleValues)[number];
